import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../AdminDashboard.css';
import SectionManager from '../components/SectionManager';

// Official brand colors for social media platforms
const OFFICIAL_SOCIAL_COLORS = {
  'Facebook': '#1877F2',
  'Instagram': '#E4405F',
  'YouTube': '#FF0000',
  'LinkedIn': '#0A66C2',
  'Twitter': '#1DA1F2',
  'Telegram': '#26A5E4',
  'WhatsApp': '#25D366',
  'Github': '#181717'
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'home');
  
  // ReactQuill Editor Configuration
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        const formData = new FormData();
        formData.append('image', file);

        try {
          const response = await axios.post(`${API}/upload/image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          const imageUrl = response.data.url;
          const quill = document.querySelector('.ql-editor').__quill;
          const range = quill.getSelection();
          quill.insertEmbed(range.index, 'image', imageUrl);
        } catch (error) {
          toast.error('Failed to upload image');
          console.error('Image upload error:', error);
        }
      }
    };
  };

  const editorModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  const editorFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet',
    'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];
  
  // Enrollment Form Wizard State
  const [showEnrollmentWizard, setShowEnrollmentWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [enrollmentData, setEnrollmentData] = useState({
    // Step 1: Personal Info
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    // Step 2: Course Selection
    course: '',
    batch: '',
    startDate: '',
    // Step 3: Address
    address: '',
    city: '',
    state: '',
    pincode: '',
    // Step 4: Payment
    paymentMethod: '',
    amount: ''
  });
  
  // States for different sections
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [mentorships, setMentorships] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [pageContent, setPageContent] = useState({});
  const [psiContent, setPsiContent] = useState({
    hero_title: 'Adhva Vibes',
    hero_subtitle: 'Your Blueprint to PSI Success',
    hero_description: 'A complete suite of data-driven test series programmes engineered for the Karnataka PSI Exam'
  });

  // Social Media Management State
  const [newSocial, setNewSocial] = useState({
    platform: '',
    url: '',
    color: '#667eea',
    order_index: 0,
    active: true
  });
  const [editingSocialId, setEditingSocialId] = useState(null);
  const [editSocialData, setEditSocialData] = useState({
    url: '',
    color: '#667eea',
    order_index: 0,
    active: true
  });
  
  // Article editor state
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    tags: '',
    status: 'draft'
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    } else {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [
        articlesRes,
        videosRes,
        enrollmentsRes,
        mentorshipsRes,
        contactsRes,
        notificationsRes,
        settingsRes,
        socialRes
      ] = await Promise.all([
        axios.get(`${API}/articles`, {}),
        axios.get(`${API}/videos`, {}),
        axios.get(`${API}/enrollments`, {}),
        axios.get(`${API}/mentorship`, {}),
        axios.get(`${API}/contact`, {}),
        axios.get(`${API}/admin/notifications`, {}),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/social-media`, {})
      ]);

      setArticles(articlesRes.data);
      setVideos(videosRes.data);
      setEnrollments(enrollmentsRes.data);
      setMentorships(mentorshipsRes.data);
      setContacts(contactsRes.data);
      setNotifications(notificationsRes.data);
      setSettings(settingsRes.data);
      setSocialLinks(socialRes.data.sort((a, b) => a.order_index - b.order_index));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Article Management
  const handleCreateArticle = async () => {
    try {
      const data = {
        ...articleForm,
        tags: articleForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      await axios.post(`${API}/articles`, data, {});
      toast.success('Article created successfully');
      setArticleForm({ title: '', content: '', excerpt: '', category: '', tags: '', status: 'draft' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create article');
    }
  };

  const handleUpdateArticle = async () => {
    try {
      const data = {
        ...articleForm,
        tags: articleForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      await axios.put(`${API}/articles/${editingArticle}`, data, {});
      toast.success('Article updated successfully');
      setEditingArticle(null);
      setArticleForm({ title: '', content: '', excerpt: '', category: '', tags: '', status: 'draft' });
      fetchData();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await axios.delete(`${API}/articles/${id}`, {});
      toast.success('Article deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  // Video Management
  const handleVideoUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await axios.post(`${API}/videos`, formData, {

        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Video uploaded successfully');
      e.target.reset();
      fetchData();
    } catch (error) {
      toast.error('Failed to upload video');
    }
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await axios.delete(`${API}/videos/${id}`, {});
      toast.success('Video deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  // Settings Management
  const handleUpdateSettings = async () => {
    try {
      await axios.put(`${API}/settings`, settings, {});
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Social Media Management
  const handleAddSocialLink = async () => {
    if (!newSocial.platform || !newSocial.url) {
      toast.error('Please fill in platform and URL');
      return;
    }

    try {
      await axios.post(`${API}/admin/social-media`, newSocial, {});
      toast.success('Social media link added successfully');
      setNewSocial({
        platform: '',
        url: '',
        order_index: 0,
        active: true
      });
      fetchData();
    } catch (error) {
      console.error('Error adding social link:', error);
      toast.error('Failed to add social media link');
    }
  };

  const handleEditSocialLink = (link) => {
    setEditingSocialId(link.id);
    setEditSocialData({
      url: link.url,
      order_index: link.order_index,
      active: link.active,
      color: link.color || '#667eea'
    });
  };

  const handleSaveSocialLink = async (linkId) => {
    try {
      await axios.put(`${API}/admin/social-media/${linkId}`, editSocialData, {});
      toast.success('Social media link updated successfully');
      setEditingSocialId(null);
      fetchData();
    } catch (error) {
      console.error('Error updating social link:', error);
      toast.error('Failed to update social media link');
    }
  };

  const handleDeleteSocialLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this social media link?')) return;

    try {
      await axios.delete(`${API}/admin/social-media/${linkId}`, {});
      toast.success('Social media link deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting social link:', error);
      toast.error('Failed to delete social media link');
    }
  };

  // Export functions
  const handleExportEnrollments = async () => {
    try {
      const response = await axios.get(`${API}/enrollments/export`, {

        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'enrollments.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  // Enrollment Wizard Functions
  const handleWizardNext = () => {
    if (wizardStep < 4) setWizardStep(wizardStep + 1);
  };

  const handleWizardPrev = () => {
    if (wizardStep > 1) setWizardStep(wizardStep - 1);
  };

  const handleEnrollmentSubmit = async () => {
    try {
      await axios.post(`${API}/enrollments`, enrollmentData, {});
      toast.success('Enrollment submitted successfully!');
      setShowEnrollmentWizard(false);
      setWizardStep(1);
      setEnrollmentData({
        fullName: '', email: '', phone: '', dateOfBirth: '',
        course: '', batch: '', startDate: '',
        address: '', city: '', state: '', pincode: '',
        paymentMethod: '', amount: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to submit enrollment');
    }
  };

  const handleExportMentorship = async () => {
    try {
      const response = await axios.get(`${API}/mentorship/export`, {

        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'mentorship.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  if (!user) return null;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)', backgroundAttachment: 'fixed' }}>
      {/* Header */}
      <div style={{ background: 'rgba(255, 255, 255, 0.98)', backdropFilter: 'blur(15px)', borderBottom: '2px solid rgba(102, 126, 234, 0.25)', padding: '1.25rem 2rem', boxShadow: '0 4px 20px rgba(102,126,234,0.15)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Adhva Academy Admin</h1>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')} 
              className="back-to-home-btn"
              style={{ borderColor: '#8b5cf6', color: '#8b5cf6', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <span>🏠</span>
              Back to Home
            </Button>
            <span style={{ color: '#667eea', fontWeight: 600 }}>Welcome, {user.name}</span>
            <Button variant="outline" onClick={handleLogout} style={{ borderColor: '#667eea', color: '#667eea', fontWeight: 600 }}>Logout</Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-11 mb-8" style={{ background: 'transparent', backdropFilter: 'none', padding: '0', borderRadius: '0', boxShadow: 'none', border: 'none', gap: '0.5rem', boxSizing: 'border-box' }}>
            <TabsTrigger value="home" data-testid="tab-home">Home</TabsTrigger>
            <TabsTrigger value="sections" data-testid="tab-sections">Page Sections</TabsTrigger>
            <TabsTrigger value="articles" data-testid="tab-articles">Articles</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="psi" data-testid="tab-psi">PSI Page</TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments</TabsTrigger>
            <TabsTrigger value="mentorship" data-testid="tab-mentorship">Mentorship</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
            <TabsTrigger value="social" data-testid="tab-social">Social</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          {/* Home Tab */}
          <TabsContent value="home">
            {/* Hero Section */}
            <div className="hero-section">
              <div className="hero-content">
                <h1 className="hero-title">Welcome to Adhva Academy</h1>
                <p className="hero-subtitle">Empowering UPSC Aspirants with Excellence</p>
                <p className="hero-description">
                  Transform your aspirations into achievements with our comprehensive study programs,
                  expert mentorship, and proven strategies for success.
                </p>
                <button className="enroll-now-btn" onClick={() => setShowEnrollmentWizard(true)}>
                  <span className="btn-icon">🎓</span>
                  Enroll Now
                  <span className="btn-arrow">→</span>
                </button>
              </div>
              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-number">{articles.length}</div>
                  <div className="stat-label">Articles</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{videos.length}</div>
                  <div className="stat-label">Videos</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{enrollments.length}</div>
                  <div className="stat-label">Students</div>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📝</div>
                <div className="stat-content">
                  <div className="stat-value">{articles.length}</div>
                  <div className="stat-name">Total Articles</div>
                  <div className="stat-trend">↑ Updated regularly</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎥</div>
                <div className="stat-content">
                  <div className="stat-value">{videos.length}</div>
                  <div className="stat-name">Video Lectures</div>
                  <div className="stat-trend">↑ Growing library</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">👨‍🎓</div>
                <div className="stat-content">
                  <div className="stat-value">{enrollments.length}</div>
                  <div className="stat-name">Active Enrollments</div>
                  <div className="stat-trend">↑ Join the community</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🤝</div>
                <div className="stat-content">
                  <div className="stat-value">{mentorships.length}</div>
                  <div className="stat-name">Mentorship Programs</div>
                  <div className="stat-trend">↑ Expert guidance</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📧</div>
                <div className="stat-content">
                  <div className="stat-value">{notifications.length}</div>
                  <div className="stat-name">Email Subscribers</div>
                  <div className="stat-trend">↑ Growing interest</div>
                </div>
              </div>
            </div>

            {/* Enrollment Wizard Modal */}
            {showEnrollmentWizard && (
              <div className="wizard-overlay" onClick={() => setShowEnrollmentWizard(false)}>
                <div className="wizard-modal" onClick={(e) => e.stopPropagation()}>
                  <button className="wizard-close" onClick={() => setShowEnrollmentWizard(false)}>✕</button>
                  
                  <div className="wizard-header">
                    <h2 className="wizard-title">Student Enrollment</h2>
                    <div className="wizard-progress">
                      <div className={`progress-step ${wizardStep >= 1 ? 'active' : ''}`}>1</div>
                      <div className={`progress-line ${wizardStep >= 2 ? 'active' : ''}`}></div>
                      <div className={`progress-step ${wizardStep >= 2 ? 'active' : ''}`}>2</div>
                      <div className={`progress-line ${wizardStep >= 3 ? 'active' : ''}`}></div>
                      <div className={`progress-step ${wizardStep >= 3 ? 'active' : ''}`}>3</div>
                      <div className={`progress-line ${wizardStep >= 4 ? 'active' : ''}`}></div>
                      <div className={`progress-step ${wizardStep >= 4 ? 'active' : ''}`}>4</div>
                    </div>
                  </div>

                  <div className="wizard-body">
                    {/* Step 1: Personal Information */}
                    {wizardStep === 1 && (
                      <div className="wizard-step">
                        <h3 className="step-title">Personal Information</h3>
                        <div className="form-grid">
                          <div className="form-field">
                            <label>Full Name *</label>
                            <input
                              type="text"
                              value={enrollmentData.fullName}
                              onChange={(e) => setEnrollmentData({...enrollmentData, fullName: e.target.value})}
                              placeholder="Enter your full name"
                              className="wizard-input"
                            />
                          </div>
                          <div className="form-field">
                            <label>Email Address *</label>
                            <input
                              type="email"
                              value={enrollmentData.email}
                              onChange={(e) => setEnrollmentData({...enrollmentData, email: e.target.value})}
                              placeholder="your.email@example.com"
                              className="wizard-input"
                            />
                          </div>
                          <div className="form-field">
                            <label>Phone Number *</label>
                            <input
                              type="tel"
                              value={enrollmentData.phone}
                              onChange={(e) => setEnrollmentData({...enrollmentData, phone: e.target.value})}
                              placeholder="+91 98765 43210"
                              className="wizard-input"
                            />
                          </div>
                          <div className="form-field">
                            <label>Date of Birth *</label>
                            <input
                              type="date"
                              value={enrollmentData.dateOfBirth}
                              onChange={(e) => setEnrollmentData({...enrollmentData, dateOfBirth: e.target.value})}
                              className="wizard-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Course Selection */}
                    {wizardStep === 2 && (
                      <div className="wizard-step">
                        <h3 className="step-title">Course Selection</h3>
                        <div className="form-grid">
                          <div className="form-field full-width">
                            <label>Select Course *</label>
                            <select
                              value={enrollmentData.course}
                              onChange={(e) => setEnrollmentData({...enrollmentData, course: e.target.value})}
                              className="wizard-input"
                            >
                              <option value="">Choose a course</option>
                              <option value="upsc-prelims">UPSC Prelims</option>
                              <option value="upsc-mains">UPSC Mains</option>
                              <option value="upsc-complete">UPSC Complete Package</option>
                              <option value="psi">Karnataka PSI</option>
                              <option value="mentorship">Personal Mentorship</option>
                            </select>
                          </div>
                          <div className="form-field">
                            <label>Preferred Batch *</label>
                            <select
                              value={enrollmentData.batch}
                              onChange={(e) => setEnrollmentData({...enrollmentData, batch: e.target.value})}
                              className="wizard-input"
                            >
                              <option value="">Select batch</option>
                              <option value="morning">Morning (6 AM - 10 AM)</option>
                              <option value="afternoon">Afternoon (2 PM - 6 PM)</option>
                              <option value="evening">Evening (6 PM - 10 PM)</option>
                              <option value="weekend">Weekend</option>
                            </select>
                          </div>
                          <div className="form-field">
                            <label>Start Date *</label>
                            <input
                              type="date"
                              value={enrollmentData.startDate}
                              onChange={(e) => setEnrollmentData({...enrollmentData, startDate: e.target.value})}
                              className="wizard-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Address Details */}
                    {wizardStep === 3 && (
                      <div className="wizard-step">
                        <h3 className="step-title">Address Details</h3>
                        <div className="form-grid">
                          <div className="form-field full-width">
                            <label>Street Address *</label>
                            <input
                              type="text"
                              value={enrollmentData.address}
                              onChange={(e) => setEnrollmentData({...enrollmentData, address: e.target.value})}
                              placeholder="House no., Street name"
                              className="wizard-input"
                            />
                          </div>
                          <div className="form-field">
                            <label>City *</label>
                            <input
                              type="text"
                              value={enrollmentData.city}
                              onChange={(e) => setEnrollmentData({...enrollmentData, city: e.target.value})}
                              placeholder="City"
                              className="wizard-input"
                            />
                          </div>
                          <div className="form-field">
                            <label>State *</label>
                            <input
                              type="text"
                              value={enrollmentData.state}
                              onChange={(e) => setEnrollmentData({...enrollmentData, state: e.target.value})}
                              placeholder="State"
                              className="wizard-input"
                            />
                          </div>
                          <div className="form-field">
                            <label>PIN Code *</label>
                            <input
                              type="text"
                              value={enrollmentData.pincode}
                              onChange={(e) => setEnrollmentData({...enrollmentData, pincode: e.target.value})}
                              placeholder="000000"
                              className="wizard-input"
                              maxLength="6"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 4: Payment */}
                    {wizardStep === 4 && (
                      <div className="wizard-step">
                        <h3 className="step-title">Payment Information</h3>
                        <div className="form-grid">
                          <div className="form-field full-width">
                            <label>Payment Method *</label>
                            <select
                              value={enrollmentData.paymentMethod}
                              onChange={(e) => setEnrollmentData({...enrollmentData, paymentMethod: e.target.value})}
                              className="wizard-input"
                            >
                              <option value="">Select payment method</option>
                              <option value="upi">UPI</option>
                              <option value="card">Credit/Debit Card</option>
                              <option value="netbanking">Net Banking</option>
                              <option value="cash">Cash</option>
                            </select>
                          </div>
                          <div className="form-field">
                            <label>Amount (₹) *</label>
                            <input
                              type="number"
                              value={enrollmentData.amount}
                              onChange={(e) => setEnrollmentData({...enrollmentData, amount: e.target.value})}
                              placeholder="0"
                              className="wizard-input"
                            />
                          </div>
                        </div>
                        <div className="payment-summary">
                          <h4>Enrollment Summary</h4>
                          <div className="summary-item">
                            <span>Name:</span>
                            <strong>{enrollmentData.fullName}</strong>
                          </div>
                          <div className="summary-item">
                            <span>Course:</span>
                            <strong>{enrollmentData.course}</strong>
                          </div>
                          <div className="summary-item">
                            <span>Batch:</span>
                            <strong>{enrollmentData.batch}</strong>
                          </div>
                          <div className="summary-item total">
                            <span>Total Amount:</span>
                            <strong>₹{enrollmentData.amount}</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="wizard-footer">
                    {wizardStep > 1 && (
                      <button className="wizard-btn-secondary" onClick={handleWizardPrev}>
                        ← Previous
                      </button>
                    )}
                    <div style={{flex: 1}}></div>
                    {wizardStep < 4 ? (
                      <button className="wizard-btn-primary" onClick={handleWizardNext}>
                        Next →
                      </button>
                    ) : (
                      <button className="wizard-btn-primary" onClick={handleEnrollmentSubmit}>
                        Submit Enrollment ✓
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Page Sections Management Tab */}
          <TabsContent value="sections">
            <SectionManager />
          </TabsContent>

          {/* Articles Tab */}
          <TabsContent value="articles">
            <Card style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', border: '1px solid rgba(102, 126, 234, 0.2)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardHeader>
                <CardTitle style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{editingArticle ? 'Edit Article' : 'Create New Article'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={articleForm.title}
                      onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                      placeholder="Article title"
                    />
                  </div>
                  <div>
                    <Label>Content</Label>
                    <ReactQuill
                      theme="snow"
                      value={articleForm.content}
                      onChange={(content) => setArticleForm({ ...articleForm, content })}
                      modules={editorModules}
                      formats={editorFormats}
                      style={{ height: '400px', marginBottom: '50px' }}
                      placeholder="Write your article content here..."
                    />
                  </div>
                  <div>
                    <Label>Excerpt (Optional)</Label>
                    <Textarea
                      value={articleForm.excerpt}
                      onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                      placeholder="Short description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category</Label>
                      <Input
                        value={articleForm.category}
                        onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                        placeholder="e.g., Strategy"
                      />
                    </div>
                    <div>
                      <Label>Tags (comma-separated)</Label>
                      <Input
                        value={articleForm.tags}
                        onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })}
                        placeholder="e.g., NCERT, Preparation"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={articleForm.status} onValueChange={(value) => setArticleForm({ ...articleForm, status: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={editingArticle ? handleUpdateArticle : handleCreateArticle}>
                    {editingArticle ? 'Update Article' : 'Create Article'}
                  </Button>
                  {editingArticle && (
                    <Button variant="outline" onClick={() => {
                      setEditingArticle(null);
                      setArticleForm({ title: '', content: '', excerpt: '', category: '', tags: '', status: 'draft' });
                    }}>
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4 admin-card">
              <CardHeader>
                <CardTitle className="admin-card-title">All Articles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {articles.map(article => (
                    <div key={article.id} className="admin-item-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ fontWeight: 600, color: '#1F2937' }}>{article.title}</h4>
                        <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>{article.status} | {article.author}</p>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button size="sm" variant="outline" onClick={() => {
                          setEditingArticle(article.id);
                          setArticleForm({
                            title: article.title,
                            content: article.content,
                            excerpt: article.excerpt || '',
                            category: article.category || '',
                            tags: article.tags.join(', '),
                            status: article.status
                          });
                        }}>
                          Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteArticle(article.id)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos">
            <Card className="admin-card">
              <CardHeader>
                <CardTitle className="admin-card-title">Upload Video</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVideoUpload} className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input name="title" required />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea name="description" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Input name="category" required placeholder="e.g., UPSC Strategy" />
                  </div>
                  <div>
                    <Label>Video File</Label>
                    <Input type="file" name="file" accept="video/*" required />
                  </div>
                  <Button type="submit">Upload Video</Button>
                </form>
              </CardContent>
            </Card>

            <Card className="mt-4 admin-card">
              <CardHeader>
                <CardTitle className="admin-card-title">All Videos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {videos.map(video => (
                    <div key={video.id} className="admin-item-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ fontWeight: 600, color: '#1F2937' }}>{video.title}</h4>
                        <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>{video.category}</p>
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteVideo(video.id)}>
                        Delete
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PSI Page Tab */}
          <TabsContent value="psi">
            <Card className="admin-card">
              <CardHeader>
                <CardTitle className="admin-card-title">Adhva Vibes PSI Page Content</CardTitle>
                <CardDescription style={{ color: '#6B7280' }}>Edit the content displayed on the PSI programme page</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Hero Title</Label>
                    <Input
                      value={psiContent.hero_title}
                      onChange={(e) => setPsiContent({ ...psiContent, hero_title: e.target.value })}
                      placeholder="Adhva Vibes"
                    />
                  </div>
                  <div>
                    <Label>Hero Subtitle</Label>
                    <Input
                      value={psiContent.hero_subtitle}
                      onChange={(e) => setPsiContent({ ...psiContent, hero_subtitle: e.target.value })}
                      placeholder="Your Blueprint to PSI Success"
                    />
                  </div>
                  <div>
                    <Label>Hero Description</Label>
                    <Textarea
                      value={psiContent.hero_description}
                      onChange={(e) => setPsiContent({ ...psiContent, hero_description: e.target.value })}
                      rows="3"
                      placeholder="A complete suite of data-driven test series programmes..."
                    />
                  </div>
                  <Button onClick={async () => {
                    try {
                      await Promise.all([
                        axios.put(`${API}/content/adhva-vibes-psi/hero-title`, `content=${encodeURIComponent(psiContent.hero_title)}`, {

                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                        }),
                        axios.put(`${API}/content/adhva-vibes-psi/hero-subtitle`, `content=${encodeURIComponent(psiContent.hero_subtitle)}`, {

                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                        }),
                        axios.put(`${API}/content/adhva-vibes-psi/hero-description`, `content=${encodeURIComponent(psiContent.hero_description)}`, {

                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                        })
                      ]);
                      toast.success('PSI page content updated');
                    } catch (error) {
                      toast.error('Failed to update content');
                    }
                  }}>
                    Save PSI Content
                  </Button>

                  <div style={{ marginTop: '2rem', padding: '1rem', background: 'var(--secondary)', borderRadius: '0.5rem' }}>
                    <h4 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Page Visibility</h4>
                    <p style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
                      Control page visibility from the <strong>Settings</strong> tab → "Adhva Vibes PSI Hidden" toggle
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments">
            <Card className="admin-card">
              <CardHeader>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <CardTitle className="admin-card-title">Enrollment Leads</CardTitle>
                  <Button onClick={handleExportEnrollments}>Export CSV</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(102, 126, 234, 0.3)' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Name</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Phone</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Email</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Course</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollments.map(enrollment => (
                        <tr key={enrollment.id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '0.75rem' }}>{enrollment.name}</td>
                          <td style={{ padding: '0.75rem' }}>{enrollment.phone}</td>
                          <td style={{ padding: '0.75rem' }}>{enrollment.email}</td>
                          <td style={{ padding: '0.75rem' }}>{enrollment.course}</td>
                          <td style={{ padding: '0.75rem' }}>{new Date(enrollment.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mentorship Tab */}
          <TabsContent value="mentorship">
            <Card className="admin-card">
              <CardHeader>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <CardTitle className="admin-card-title">Mentorship Applications</CardTitle>
                  <Button onClick={handleExportMentorship}>Export CSV</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(102, 126, 234, 0.3)' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Name</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Phone</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Email</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Education</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Status</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#667eea' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mentorships.map(app => (
                        <tr key={app.id} style={{ borderBottom: '1px solid rgba(102, 126, 234, 0.1)' }}>
                          <td style={{ padding: '0.75rem', color: '#1F2937' }}>{app.name}</td>
                          <td style={{ padding: '0.75rem', color: '#1F2937' }}>{app.phone}</td>
                          <td style={{ padding: '0.75rem', color: '#1F2937' }}>{app.email}</td>
                          <td style={{ padding: '0.75rem', color: '#1F2937' }}>{app.education_level}</td>
                          <td style={{ padding: '0.75rem' }}>
                            <span style={{ 
                              padding: '0.25rem 0.75rem', 
                              borderRadius: '12px', 
                              fontSize: '0.875rem',
                              background: app.status === 'approved' ? '#D1FAE5' : app.status === 'rejected' ? '#FEE2E2' : '#FEF3C7',
                              color: app.status === 'approved' ? '#065F46' : app.status === 'rejected' ? '#991B1B' : '#92400E'
                            }}>
                              {app.status}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem' }}>{new Date(app.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card className="admin-card">
              <CardHeader>
                <CardTitle className="admin-card-title">Contact Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contacts.map(contact => (
                    <div key={contact.id} className="admin-item-card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <h4 style={{ fontWeight: 600, color: '#1F2937' }}>{contact.name}</h4>
                        <span style={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500 }}>{new Date(contact.created_at).toLocaleDateString()}</span>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>
                        {contact.email} {contact.phone && `| ${contact.phone}`}
                      </p>
                      <p style={{ color: '#374151' }}>{contact.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="admin-card">
              <CardHeader>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <CardTitle className="admin-card-title">Email Notifications</CardTitle>
                    <CardDescription style={{ color: '#6B7280', marginTop: '0.5rem' }}>
                      Users who signed up for launch notifications
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      // Export to CSV
                      const csvContent = [
                        ['Email', 'Page', 'Source', 'Date'],
                        ...notifications.map(n => [
                          n.email,
                          n.page || 'N/A',
                          n.source || 'N/A',
                          new Date(n.created_at).toLocaleDateString()
                        ])
                      ].map(row => row.join(',')).join('\n');
                      
                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `email-notifications-${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                      toast.success('Email list exported successfully!');
                    }}
                    className="btn-primary"
                    style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Export to CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Stats Summary */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '1rem', 
                  marginBottom: '2rem' 
                }}>
                  <div style={{ 
                    padding: '1.5rem', 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    borderRadius: '0.75rem',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                      {notifications.length}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Subscribers</div>
                  </div>
                  <div style={{ 
                    padding: '1.5rem', 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                    borderRadius: '0.75rem',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                      {[...new Set(notifications.map(n => n.email))].length}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Unique Emails</div>
                  </div>
                  <div style={{ 
                    padding: '1.5rem', 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
                    borderRadius: '0.75rem',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                      {notifications.filter(n => n.page === 'current-affairs-test').length}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Current Affairs</div>
                  </div>
                </div>

                {/* Notifications Table */}
                <div style={{ 
                  background: 'white', 
                  borderRadius: '0.75rem', 
                  border: '1px solid #E5E7EB',
                  overflow: 'hidden'
                }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                        <tr>
                          <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>
                            Email
                          </th>
                          <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>
                            Page
                          </th>
                          <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>
                            Source
                          </th>
                          <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {notifications.length === 0 ? (
                          <tr>
                            <td colSpan="4" style={{ 
                              padding: '3rem', 
                              textAlign: 'center', 
                              color: '#9CA3AF' 
                            }}>
                              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📧</div>
                              <div style={{ fontSize: '1.125rem', fontWeight: 500 }}>No notifications yet</div>
                              <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                Email subscriptions will appear here when users sign up
                              </div>
                            </td>
                          </tr>
                        ) : (
                          notifications.map((notification, index) => (
                            <tr 
                              key={notification.id} 
                              style={{ 
                                borderBottom: index < notifications.length - 1 ? '1px solid #E5E7EB' : 'none',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <td style={{ padding: '1rem', color: '#1F2937', fontWeight: 500 }}>
                                {notification.email}
                              </td>
                              <td style={{ padding: '1rem', color: '#6B7280' }}>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  background: '#EEF2FF',
                                  color: '#4F46E5',
                                  borderRadius: '9999px',
                                  fontSize: '0.875rem',
                                  fontWeight: 500
                                }}>
                                  {notification.page || 'N/A'}
                                </span>
                              </td>
                              <td style={{ padding: '1rem', color: '#6B7280', fontSize: '0.875rem' }}>
                                {notification.source || 'N/A'}
                              </td>
                              <td style={{ padding: '1rem', color: '#6B7280', fontSize: '0.875rem' }}>
                                {new Date(notification.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Quick Copy All Emails */}
                {notifications.length > 0 && (
                  <div style={{ 
                    marginTop: '2rem', 
                    padding: '1.5rem', 
                    background: '#F9FAFB', 
                    borderRadius: '0.75rem',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h4 style={{ fontWeight: 600, color: '#374151' }}>All Email Addresses</h4>
                      <Button
                        onClick={() => {
                          const emails = [...new Set(notifications.map(n => n.email))].join(', ');
                          navigator.clipboard.writeText(emails);
                          toast.success('All emails copied to clipboard!');
                        }}
                        className="btn-secondary"
                        style={{ fontSize: '0.875rem' }}
                      >
                        📋 Copy All
                      </Button>
                    </div>
                    <div style={{ 
                      padding: '1rem', 
                      background: 'white', 
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#6B7280',
                      maxHeight: '100px',
                      overflowY: 'auto',
                      fontFamily: 'monospace'
                    }}>
                      {[...new Set(notifications.map(n => n.email))].join(', ')}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social Tab */}
          <TabsContent value="social">
            <Card className="admin-card">
              <CardHeader>
                <CardTitle className="admin-card-title">Social Media Links</CardTitle>
                <CardDescription style={{ color: '#6B7280' }}>Manage social media links displayed in the header</CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', fontWeight: 600 }}>Add New Social Media Link</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto 1fr 1fr auto', gap: '1rem', alignItems: 'center' }}>
                    <select
                      value={newSocial.platform}
                      onChange={(e) => {
                        const platform = e.target.value;
                        const officialColor = OFFICIAL_SOCIAL_COLORS[platform] || '#667eea';
                        setNewSocial({ ...newSocial, platform, color: officialColor });
                      }}
                      style={{
                        padding: '0.75rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.5rem',
                        fontSize: '0.95rem',
                        background: 'var(--background)',
                        color: 'var(--foreground)'
                      }}
                    >
                      <option value="">Select Platform</option>
                      <option value="Facebook">Facebook</option>
                      <option value="Instagram">Instagram</option>
                      <option value="YouTube">YouTube</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Twitter">Twitter</option>
                      <option value="Telegram">Telegram</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="Github">Github</option>
                    </select>
                    <input
                      type="text"
                      placeholder="https://facebook.com/yourpage"
                      value={newSocial.url}
                      onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.5rem',
                        fontSize: '0.95rem',
                        background: 'var(--background)',
                        color: 'var(--foreground)'
                      }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="color"
                        value={newSocial.color}
                        onChange={(e) => setNewSocial({ ...newSocial, color: e.target.value })}
                        style={{
                          width: '50px',
                          height: '42px',
                          border: '2px solid var(--border)',
                          borderRadius: '0.5rem',
                          cursor: 'pointer'
                        }}
                        title="Choose icon color"
                      />
                      <div style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '0.25rem',
                        backgroundColor: newSocial.color,
                        border: '2px solid #e5e7eb'
                      }}></div>
                    </div>
                    <input
                      type="number"
                      placeholder="Order"
                      value={newSocial.order_index}
                      onChange={(e) => setNewSocial({ ...newSocial, order_index: parseInt(e.target.value) || 0 })}
                      style={{
                        padding: '0.75rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.5rem',
                        fontSize: '0.95rem',
                        background: 'var(--background)',
                        color: 'var(--foreground)'
                      }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={newSocial.active}
                        onChange={(e) => setNewSocial({ ...newSocial, active: e.target.checked })}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Active</span>
                    </label>
                    <button
                      className="btn-primary"
                      onClick={handleAddSocialLink}
                      style={{ padding: '0.75rem 1.5rem', whiteSpace: 'nowrap' }}
                    >
                      Add Link
                    </button>
                  </div>
                </div>

                <div>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', fontWeight: 600 }}>Existing Social Media Links</h3>
                  {socialLinks.length === 0 ? (
                    <p style={{ color: '#6B7280', padding: '2rem', textAlign: 'center', background: 'var(--secondary)', borderRadius: '0.5rem' }}>
                      No social media links added yet. Add your first link above.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {socialLinks.map((link) => (
                        <div
                          key={link.id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: editingSocialId === link.id ? '120px 1fr auto 80px 100px auto' : '120px 1fr auto 80px 100px auto',
                            gap: '1rem',
                            alignItems: 'center',
                            padding: '1rem',
                            background: 'var(--card)',
                            border: '2px solid var(--border)',
                            borderRadius: '0.5rem',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <div style={{ 
                            fontWeight: 600, 
                            color: link.color || '#667eea',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <div style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '0.25rem',
                              backgroundColor: link.color || '#667eea',
                              border: '2px solid #e5e7eb'
                            }}></div>
                            {link.platform}
                          </div>
                          {editingSocialId === link.id ? (
                            <>
                              <input
                                type="text"
                                value={editSocialData.url}
                                onChange={(e) => setEditSocialData({ ...editSocialData, url: e.target.value })}
                                style={{
                                  padding: '0.5rem',
                                  border: '2px solid var(--accent)',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.9rem',
                                  background: 'var(--background)',
                                  color: 'var(--foreground)'
                                }}
                              />
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                  type="color"
                                  value={editSocialData.color || '#667eea'}
                                  onChange={(e) => setEditSocialData({ ...editSocialData, color: e.target.value })}
                                  style={{
                                    width: '50px',
                                    height: '38px',
                                    border: '2px solid var(--border)',
                                    borderRadius: '0.375rem',
                                    cursor: 'pointer'
                                  }}
                                  title="Choose icon color"
                                />
                                <div style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '0.25rem',
                                  backgroundColor: editSocialData.color || '#667eea',
                                  border: '2px solid #e5e7eb'
                                }}></div>
                              </div>
                              <input
                                type="number"
                                value={editSocialData.order_index}
                                onChange={(e) => setEditSocialData({ ...editSocialData, order_index: parseInt(e.target.value) || 0 })}
                                style={{
                                  padding: '0.5rem',
                                  border: '2px solid var(--accent)',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.9rem',
                                  background: 'var(--background)',
                                  color: 'var(--foreground)',
                                  textAlign: 'center'
                                }}
                              />
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={editSocialData.active}
                                  onChange={(e) => setEditSocialData({ ...editSocialData, active: e.target.checked })}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '0.9rem' }}>Active</span>
                              </label>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: '0.9rem', color: 'var(--muted-foreground)', wordBreak: 'break-all' }}>
                                {link.url}
                              </div>
                              <div style={{ 
                                fontSize: '0.85rem', 
                                color: '#6B7280',
                                fontFamily: 'monospace'
                              }}>
                                {link.color || '#667eea'}
                              </div>
                              <div style={{ textAlign: 'center', color: 'var(--muted-foreground)' }}>
                                {link.order_index}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div
                                  style={{
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '1rem',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    background: link.active ? '#10B981' : '#6B7280',
                                    color: 'white'
                                  }}
                                >
                                  {link.active ? 'Active' : 'Inactive'}
                                </div>
                              </div>
                            </>
                          )}
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            {editingSocialId === link.id ? (
                              <>
                                <button
                                  onClick={() => handleSaveSocialLink(link.id)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#10B981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 600
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => setEditingSocialId(null)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#6B7280',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 600
                                  }}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditSocialLink(link)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: 'var(--accent)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 600
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteSocialLink(link.id)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#EF4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 600
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            {settings && (
              <div>
                <div className="settings-section">
                  <h3>Page Toggles</h3>
                  
                  <div className="toggle-item">
                    <div className="toggle-item-content">
                      <div className="toggle-item-title">Current Affairs Redirect</div>
                      <div className="toggle-item-description">Redirect to external site</div>
                    </div>
                    <div className="custom-toggle-wrapper">
                      <button
                        className={`custom-toggle ${settings.current_affairs_redirect ? 'active' : ''}`}
                        onClick={() => setSettings({ ...settings, current_affairs_redirect: !settings.current_affairs_redirect })}
                      >
                        <span className="toggle-label">{settings.current_affairs_redirect ? 'ON' : 'OFF'}</span>
                        <span className="toggle-thumb"></span>
                      </button>
                    </div>
                  </div>

                  <div className="toggle-item">
                    <div className="toggle-item-content">
                      <div className="toggle-item-title">Blog Coming Soon</div>
                      <div className="toggle-item-description">Show coming soon page</div>
                    </div>
                    <div className="custom-toggle-wrapper">
                      <button
                        className={`custom-toggle ${settings.blog_show_coming_soon ? 'active' : ''}`}
                        onClick={() => setSettings({ ...settings, blog_show_coming_soon: !settings.blog_show_coming_soon })}
                      >
                        <span className="toggle-label">{settings.blog_show_coming_soon ? 'COMING SOON' : 'LIVE'}</span>
                        <span className="toggle-thumb"></span>
                      </button>
                    </div>
                  </div>

                  <div className="toggle-item">
                    <div className="toggle-item-content">
                      <div className="toggle-item-title">Videos Coming Soon</div>
                      <div className="toggle-item-description">Show coming soon page</div>
                    </div>
                    <div className="custom-toggle-wrapper">
                      <button
                        className={`custom-toggle ${settings.videos_show_coming_soon ? 'active' : ''}`}
                        onClick={() => setSettings({ ...settings, videos_show_coming_soon: !settings.videos_show_coming_soon })}
                      >
                        <span className="toggle-label">{settings.videos_show_coming_soon ? 'COMING SOON' : 'LIVE'}</span>
                        <span className="toggle-thumb"></span>
                      </button>
                    </div>
                  </div>

                  <div className="toggle-item">
                    <div className="toggle-item-content">
                      <div className="toggle-item-title">Adhva Vibes PSI Page</div>
                      <div className="toggle-item-description">Control PSI page visibility</div>
                    </div>
                    <div className="custom-toggle-wrapper">
                      <button
                        className={`custom-toggle ${(settings.adhva_vibes_psi_hidden || false) ? '' : 'active'}`}
                        onClick={() => setSettings({ ...settings, adhva_vibes_psi_hidden: !(settings.adhva_vibes_psi_hidden || false) })}
                      >
                        <span className="toggle-label">{(settings.adhva_vibes_psi_hidden || false) ? 'HIDDEN' : 'VISIBLE'}</span>
                        <span className="toggle-thumb"></span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="settings-section">
                  <h3>General Settings</h3>
                  
                  <div className="settings-input-group">
                    <label className="settings-label">Site Title</label>
                    <input
                      type="text"
                      className="settings-input"
                      value={settings.site_title}
                      onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
                      placeholder="Enter site title"
                    />
                  </div>

                  <div className="settings-input-group">
                    <label className="settings-label">Site Description</label>
                    <textarea
                      className="settings-textarea"
                      value={settings.site_description}
                      onChange={(e) => setSettings({ ...settings, site_description: e.target.value })}
                      placeholder="Enter site description"
                    />
                  </div>

                  <div className="settings-row">
                    <div className="settings-input-group">
                      <label className="settings-label">Contact Phone</label>
                      <input
                        type="tel"
                        className="settings-input"
                        value={settings.contact_phone}
                        onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                        placeholder="+91 98765 43210"
                      />
                    </div>

                    <div className="settings-input-group">
                      <label className="settings-label">Contact Email</label>
                      <input
                        type="email"
                        className="settings-input"
                        value={settings.contact_email}
                        onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                        placeholder="info@adhvaacademy.com"
                      />
                    </div>
                  </div>

                  <div className="settings-input-group">
                    <label className="settings-label">WhatsApp Number</label>
                    <input
                      type="tel"
                      className="settings-input"
                      value={settings.contact_whatsapp}
                      onChange={(e) => setSettings({ ...settings, contact_whatsapp: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div className="settings-input-group">
                    <label className="settings-label">Address</label>
                    <textarea
                      className="settings-textarea"
                      value={settings.contact_address}
                      onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
                      placeholder="Enter complete address"
                      rows="3"
                    />
                  </div>

                  {/* Hero Video Settings */}
                  <div className="settings-section" style={{ marginTop: '2rem' }}>
                    <h3>Hero Background</h3>
                    <p style={{ color: 'var(--muted-foreground)', marginBottom: '1rem' }}>
                      Choose between a static background image or an autoplay video for the hero section.
                    </p>

                    {/* Background Type Toggle */}
                    <div className="settings-input-group">
                      <label className="settings-label">Background Type</label>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, hero_background_type: 'image' })}
                          style={{
                            padding: '0.75rem 1.5rem',
                            border: (settings.hero_background_type === 'image' || !settings.hero_background_type) 
                              ? '2px solid var(--accent)' 
                              : '2px solid #E5E7EB',
                            background: (settings.hero_background_type === 'image' || !settings.hero_background_type)
                              ? 'var(--accent)'
                              : 'white',
                            color: (settings.hero_background_type === 'image' || !settings.hero_background_type)
                              ? 'white'
                              : '#6B7280',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          🖼️ Static Image
                        </button>
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, hero_background_type: 'video' })}
                          style={{
                            padding: '0.75rem 1.5rem',
                            border: settings.hero_background_type === 'video'
                              ? '2px solid var(--accent)'
                              : '2px solid #E5E7EB',
                            background: settings.hero_background_type === 'video'
                              ? 'var(--accent)'
                              : 'white',
                            color: settings.hero_background_type === 'video'
                              ? 'white'
                              : '#6B7280',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          🎥 Video
                        </button>
                      </div>
                      <small style={{ color: '#6B7280', marginTop: '0.5rem', display: 'block' }}>
                        {(settings.hero_background_type === 'image' || !settings.hero_background_type)
                          ? 'Using default static background image (hardcoded)'
                          : 'Upload a video or paste a YouTube URL below'}
                      </small>
                    </div>

                    {/* Video Settings - Only show when video type is selected */}
                    {settings.hero_background_type === 'video' && (
                      <>
                        <div className="settings-input-group">
                          <label className="settings-label">Video URL</label>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                            <input
                              type="text"
                              className="settings-input"
                              style={{ flex: 1 }}
                              value={settings.hero_video_url || ''}
                              onChange={(e) => setSettings({ ...settings, hero_video_url: e.target.value })}
                              placeholder="YouTube: https://youtube.com/watch?v=... or Upload: /uploads/videos/..."
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              <input
                                type="file"
                                accept="video/*"
                                id="video-upload"
                                style={{ display: 'none' }}
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  
                                  try {
                                    toast.info('Uploading video...');
                                    const res = await axios.post(`${API}/admin/upload-video`, formData, {

                                      headers: { 'Content-Type': 'multipart/form-data' }
                                    });
                                    setSettings({ ...settings, hero_video_url: res.data.url });
                                    toast.success('Video uploaded successfully!');
                                  } catch (error) {
                                    console.error('Upload error:', error);
                                    toast.error('Failed to upload video');
                                  }
                                  e.target.value = '';
                                }}
                              />
                          <button
                            type="button"
                            onClick={() => document.getElementById('video-upload')?.click()}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'var(--accent)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            📤 Upload Video
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="settings-input-group">
                      <label className="settings-label">Poster Image URL (optional)</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                        <input
                          type="text"
                          className="settings-input"
                          style={{ flex: 1 }}
                          value={settings.hero_video_poster || ''}
                          onChange={(e) => setSettings({ ...settings, hero_video_poster: e.target.value })}
                          placeholder="https://cdn.example.com/hero-poster.jpg"
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <input
                            type="file"
                            accept="image/*"
                            id="poster-upload"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              try {
                                toast.info('Uploading poster...');
                                const res = await axios.post(`${API}/admin/upload-poster`, formData, {

                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                setSettings({ ...settings, hero_video_poster: res.data.url });
                                toast.success('Poster uploaded successfully!');
                              } catch (error) {
                                console.error('Upload error:', error);
                                toast.error('Failed to upload poster');
                              }
                              e.target.value = '';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => document.getElementById('poster-upload')?.click()}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'var(--accent)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            📤 Upload Poster
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="settings-row">
                      <div className="toggle-item" style={{ flex: 1 }}>
                        <div className="toggle-item-content">
                          <div className="toggle-item-title">Autoplay</div>
                          <div className="toggle-item-description">Start playing automatically</div>
                        </div>
                        <div className="custom-toggle-wrapper">
                          <button
                            className={`custom-toggle ${(settings.hero_video_autoplay ?? true) ? 'active' : ''}`}
                            onClick={() => setSettings({ ...settings, hero_video_autoplay: !(settings.hero_video_autoplay ?? true) })}
                          >
                            <span className="toggle-label">{(settings.hero_video_autoplay ?? true) ? 'ON' : 'OFF'}</span>
                            <span className="toggle-thumb"></span>
                          </button>
                        </div>
                      </div>
                      <div className="toggle-item" style={{ flex: 1 }}>
                        <div className="toggle-item-content">
                          <div className="toggle-item-title">Muted</div>
                          <div className="toggle-item-description">Required for mobile autoplay</div>
                        </div>
                        <div className="custom-toggle-wrapper">
                          <button
                            className={`custom-toggle ${(settings.hero_video_muted ?? true) ? 'active' : ''}`}
                            onClick={() => setSettings({ ...settings, hero_video_muted: !(settings.hero_video_muted ?? true) })}
                          >
                            <span className="toggle-label">{(settings.hero_video_muted ?? true) ? 'ON' : 'OFF'}</span>
                            <span className="toggle-thumb"></span>
                          </button>
                        </div>
                      </div>
                      <div className="toggle-item" style={{ flex: 1 }}>
                        <div className="toggle-item-content">
                          <div className="toggle-item-title">Loop</div>
                          <div className="toggle-item-description">Repeat video continuously</div>
                        </div>
                        <div className="custom-toggle-wrapper">
                          <button
                            className={`custom-toggle ${(settings.hero_video_loop ?? true) ? 'active' : ''}`}
                            onClick={() => setSettings({ ...settings, hero_video_loop: !(settings.hero_video_loop ?? true) })}
                          >
                            <span className="toggle-label">{(settings.hero_video_loop ?? true) ? 'ON' : 'OFF'}</span>
                            <span className="toggle-thumb"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                      </>
                    )}
                  </div>

                  <button className="settings-save-btn" onClick={handleUpdateSettings}>
                    💾 Save Settings
                  </button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
